import { useId } from "react";
import { Box, Checkbox, FormControlLabel, Stack, useTheme } from "@mui/material";
import { Button, Typography } from "@foodpilot/foods";
import { formatFacetCount } from "./formatFacetCount";
import type { FacetValue } from "./types";

type FacetValueRowProps = {
    value: FacetValue;
    isChecked: boolean;
    onlyLabel: string;
    onlyActionLabel: string;
    onToggle: () => void;
    onSelectOnly: () => void;
};

const ONLY_ACTION_CLASS = "FacetValueRow-onlyAction";

export const FacetValueRow = (props: FacetValueRowProps) => {
    const { value, isChecked, onlyLabel, onlyActionLabel, onToggle, onSelectOnly } = props;
    const theme = useTheme();
    const countId = useId();

    return (
        <Stack
            direction="row"
            alignItems="center"
            paddingY={theme.spacing(0.125)}
            paddingX={theme.spacing(0.5)}
            sx={{
                borderRadius: theme.spacing(0.5),
                "&:hover": { backgroundColor: theme.palette.grey[400] },
                [`&:hover .${ONLY_ACTION_CLASS}, &:focus-within .${ONLY_ACTION_CLASS}`]: {
                    opacity: 1,
                    pointerEvents: "auto",
                },
                [`@media (hover: none)`]: {
                    [`& .${ONLY_ACTION_CLASS}`]: { opacity: 1, pointerEvents: "auto" },
                },
            }}
        >
            <Box flexGrow={1} minWidth={0} sx={{ overflow: "hidden" }}>
                <FormControlLabel
                    checked={isChecked}
                    onChange={onToggle}
                    control={
                        <Checkbox
                            size="small"
                            color="primary"
                            inputProps={{ "aria-describedby": countId }}
                            sx={{ padding: theme.spacing(0.375) }}
                        />
                    }
                    label={
                        <Stack direction="row" alignItems="center" gap={theme.spacing(0.75)} minWidth={0}>
                            {value.tone !== undefined && (
                                <Box
                                    flexShrink={0}
                                    sx={{
                                        width: theme.spacing(0.375),
                                        height: theme.spacing(1.5),
                                        borderRadius: theme.spacing(0.5),
                                        backgroundColor: theme.palette[value.tone].main,
                                    }}
                                />
                            )}
                            <Typography variant="small-regular" noWrap minWidth={0} lineHeight={1.35}>
                                {value.label}
                            </Typography>
                        </Stack>
                    }
                    sx={{
                        display: "flex",
                        minWidth: 0,
                        marginLeft: 0,
                        marginRight: 0,
                        gap: theme.spacing(0.25),
                        "& .MuiFormControlLabel-label": { display: "flex", minWidth: 0, overflow: "hidden" },
                    }}
                />
            </Box>
            <Stack direction="row" alignItems="center" flexShrink={0} position="relative">
                <Button
                    variant="text"
                    size="small"
                    className={ONLY_ACTION_CLASS}
                    aria-label={onlyActionLabel}
                    onClick={(event) => {
                        event.stopPropagation();
                        onSelectOnly();
                    }}
                    sx={{
                        position: "absolute",
                        right: "100%",
                        top: "50%",
                        transform: "translateY(-50%)",
                        opacity: 0,
                        pointerEvents: "none",
                        minWidth: "auto",
                        height: "auto",
                        paddingX: theme.spacing(0.5),
                        paddingY: 0,
                        marginRight: theme.spacing(0.75),
                        whiteSpace: "nowrap",
                        borderRadius: theme.spacing(0.5),
                        backgroundColor: theme.palette.grey[400],
                        "&:hover": { backgroundColor: theme.palette.grey[400] },
                        "&:focus-visible": { opacity: 1, pointerEvents: "auto" },
                    }}
                >
                    <Typography variant="small-medium">{onlyLabel}</Typography>
                </Button>
                <Typography id={countId} variant="small-regular" color="text.secondary">
                    {formatFacetCount(value.count)}
                </Typography>
            </Stack>
        </Stack>
    );
};
